import store from '@state/store'
import { apiChat } from '@modules/aawz-intelligence-v2/api/apiChat'
import { falaDoAtalho } from '@modules/aawz-intelligence-v2/utils/comando-na-fala'

/**
 * [spec-013 T003] Transporte do atalho de negócio (`/panorama`,
 * `/pré-reunião <cliente>`) — fala com **`/chat`, sempre**, nunca o envio
 * comum.
 *
 * ## Por que não `apiChat.sendMessage()`
 *
 * `sendMessage()` é chamado por `despacharMensagem` (`view-chat.vue`), que
 * bifurca para `POST /runs` quando a feature `IA - Agente` está ligada
 * (`view-chat.vue:1666-1672` — "a única bifurcação do envio"). Um atalho
 * ali dentro viraria run do worker e o desenho SÍNCRONO do progresso
 * (`plano`, spec-008) cairia. Por isso este módulo fala DIRETO com o
 * miolo de transporte do `apiChat` — `_pedirEDrenar` — que sempre resolve
 * `/chat` (`_endpointDoChat()` devolve `this.endpoints.chat`, sem ramo
 * nenhum por persona/modo desde [spec-010 T030]).
 *
 * ## Reuso, não reescrita
 *
 * `resetChatState()`, `_abrirAbortController()`, `_pedirEDrenar()` e
 * `_contextoDeTela()` são os MESMOS que `sendMessage`/`sendToolOutput`
 * usam — cancelamento de stream em voo, sequência de stream ativo, drenagem
 * SSE e recorte de tela seguem uma fonte só. Nenhum fetch/EventSource novo
 * nasce aqui.
 *
 * ## O campo é `comando`, nunca `atalho`
 *
 * Contrato de `T001` (`contratos/entrada.py`): o corpo do dispatch usa
 * `comando: { slug, argumento }` — incompatível com `query` preenchida.
 *
 * ## [spec-013 T024] A bolha do usuário é DESTE lado também
 *
 * O servidor grava a fala do comando na conversa antes de responder
 * (`core/atalhos/turno.py`, com a justificativa explícita: "sem isto a
 * conversa nasceria com resposta sem pergunta"). O front fazia o mesmo só
 * no envio de TEXTO (`apiChat.js::sendMessage`), então ao vivo o
 * `/panorama` desenhava plano, resumo e cartão sem nenhuma bolha dizendo o
 * que tinha sido pedido — e um F5 fazia a pergunta aparecer, vinda do
 * banco. O fio contradizia o histórico do MESMO turno. Por isso a mensagem
 * do usuário entra aqui, no mesmo ponto e na mesma ordem de
 * `sendMessage` (depois do `resetChatState`, antes do
 * `_abrirAbortController`), com o texto de `falaDoAtalho` — o mesmo que o
 * servidor grava.
 */

/**
 * Rótulo de reserva para o caso degradado em que o evento chega sem `label`
 * (catálogo malformado — o item do menu já teria nascido sem nome, ver
 * `comandoDoAtalho`). Melhor uma bolha com `/pre-reuniao` do que turno sem
 * pergunta: o pior desfecho aqui é o acento voltar no F5, não a tela mentir.
 */
function rotuloDoAtalho(slug, label) {
  return label || '/' + slug
}

/**
 * ★ [D4, owner 21/09/2026] `motor` é o `APIChat` DA CONVERSA — o mesmo que
 * `view-chat.vue::motor` entrega a todo o resto da tela
 * (`vivaAtual ? vivaAtual.apiChat : apiChat`). Sem ele este transporte falava com o
 * SINGLETON, cujo `storeNamespace` é o estático, enquanto a tela lia o da conversa viva
 * (`iaConversa:<uuid>`).
 *
 * O que isso produzia, medido NA TELA lendo `messages` nos dois namespaces logo após
 * enviar `/panorama`:
 *
 *     {"estatico": 2, "vivos": {"iaConversa:808e2a3b-": 0}, "selecionada": "808e2a3b-…"}
 *
 * As duas mensagens do turno num namespace, a tela lendo o outro — e `EmptyMessages`
 * decide "vazio" pela CONTAGEM real de mensagens. O usuário via "Olá / Como posso te
 * ajudar hoje?" de pé, com a conversa só em "Recentes": *"digito /panorama e não faz
 * nada"*. O CONTROLE que fechou o caso foi o envio de TEXTO normal no mesmo percurso,
 * que despacha em `iaConversa:<uuid>/…` e sai do estado vazio.
 *
 * O default é o singleton de propósito: os outros chamadores (e o reenvio registrado
 * abaixo, que repete ESTA função) continuam byte a byte como eram.
 */
async function sendAtalho({ slug, label, argumento, conversationId, motor } = {}) {
  const chat = motor || apiChat
  if (!slug) {
    return chat.buildErrorResponse({
      message: 'Não foi possível identificar o atalho. Tente novamente.',
      status: 400,
    })
  }
  chat.resetChatState()
  // Depois do reset e antes de qualquer await — mesmo ponto em que
  // `sendMessage` grava o dele: é este registro que o "Tentar de novo" da
  // bolha de erro repete, e sem ele o botão reenviaria a última fala de TEXTO
  // da conversa (T024). `sendAtalho` se registra como o despachante do
  // reenvio; `apiChat` não pode importar este módulo (ciclo).
  chat.registrarEnvioDeAtalho(
    { slug: slug, label: label, argumento: argumento || null, conversationId: conversationId || null, motor: motor },
    sendAtalho
  )
  store.dispatch(chat.storeNamespace + '/addMessage', {
    id: null,
    role: 'user',
    content: falaDoAtalho(rotuloDoAtalho(slug, label), argumento),
  })
  const meuStream = chat._abrirAbortController()
  const body = {
    comando: { slug: slug, argumento: argumento || null },
    conversation_id: conversationId || null,
  }
  Object.assign(body, chat._contextoDeTela())
  // (`await` + `return` separados: `require-await` × `no-return-await`,
  // mesmo padrão de `apiChat.js::sendMessage`.)
  const desfecho = await chat._pedirEDrenar(body, meuStream)
  return desfecho
}

const apiChatAtalhos = { sendAtalho }

// `falaDoAtalho` é re-exportado daqui para quem fala com o transporte do atalho
// (`apiChatAtalhos.unit.js`) não precisar conhecer o util.
export { falaDoAtalho } from '@modules/aawz-intelligence-v2/utils/comando-na-fala'
export { apiChatAtalhos, sendAtalho }
